// riksu's tooltip: the generated one, with the motion it was written for.
//
// # What the generated component does instead
//
// `components/ui/tooltip.tsx` carries `animate-in fade-in-0 zoom-in-95` and four
// `slide-in-from-*` classes. Those come from `tw-animate-css`, which this repository
// deliberately does not install — `motion.ts` explains why at length — so they resolve to
// nothing and the tooltip appears and vanishes with no transition at all.
//
// Meanwhile `tooltipMotion` was written, tokenised, given a reduced-motion answer and
// applied to nothing. This is its call site.
//
// # The PROVIDER was missing, and it is not decoration
//
// Nothing in the product mounted a `TooltipProvider`, so every tooltip used the default
// delay in isolation and none of them shared the one behaviour that makes a toolbar feel
// fast: once a tooltip is open, moving to its neighbour should show the next one
// IMMEDIATELY. Base UI signals that with `data-instant`, `tooltipMotion` already branches
// on it, and without a provider the signal never fires.
//
// # Never the only place a fact lives
//
// A tooltip repeats an accessible name; it does not add to one. A tooltip carrying
// information the name does not is information a screen reader never receives, and it is
// unreachable on a touch screen besides.

import type { ComponentProps } from "react";
import {
  Tooltip as Base,
  TooltipContent as BaseContent,
  TooltipProvider as BaseProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { tooltipMotion } from "./motion";

export { TooltipTrigger };
export const Tooltip = Base;

/**
 * Mounted once, at the root.
 *
 * `delay` is the wait before the FIRST tooltip. It exists so that crossing a toolbar on
 * the way somewhere else does not light up six of them; it is not there to make the
 * product feel considered, and past about half a second it reads as the tooltip being
 * broken rather than delayed.
 */
export function TooltipProvider(props: ComponentProps<typeof BaseProvider>) {
  return <BaseProvider delay={400} {...props} />;
}

export function TooltipContent({ className, ...props }: ComponentProps<typeof BaseContent>) {
  return (
    <BaseContent
      className={cn(
        // The dead keyframe classes the generated component ships are overridden rather
        // than removed: `shadcn add` would put them back, and a transition beats a
        // keyframe that paints nothing anyway.
        "animate-none",
        tooltipMotion,
        // BOTH HALVES, together. See the note above: naming only the size let a class
        // merge take the colour with it, and the result was invisible rather than
        // wrong-looking, which is why it survived every gate this repository has.
        "z-(--layer-popover) bg-surface-shell text-shell-caption text-on-shell",
        className,
      )}
      {...props}
    />
  );
}
